import { useEffect } from "react";

interface HotkeyOptions {
  /** Cmd auf Mac / Ctrl auf Windows-Linux. */
  meta?: boolean;
  /** Feuert auch, wenn der Fokus in einem Eingabefeld liegt (z. B. für Cmd+K). */
  allowInInputs?: boolean;
  enabled?: boolean;
  /**
   * Hält das Ereignis hier an, statt es weiter an `window` zu reichen. Gedacht
   * für Griffe, die eine Fremdkomponente bereits belegt: `SidebarProvider`
   * (components/ui/sidebar.tsx) hängt sein eigenes Cmd+B ungefragt ans
   * `window`, und bei zwei Leisten hängen es zwei. Dieser Lauscher sitzt am
   * `document` und damit im Blasenweg davor — wer hier anhält, entscheidet
   * allein, was die Taste tut. Siehe SidebarShell.tsx.
   */
  stopPropagation?: boolean;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
}

/** Registriert ein globales Tastatur-Shortcut. Einzelbuchstaben-Shortcuts (z. B. "/") ignorieren Eingabefelder automatisch. */
export function useHotkey(key: string, handler: (event: KeyboardEvent) => void, options: HotkeyOptions = {}) {
  const { meta = false, allowInInputs = false, enabled = true, stopPropagation = false } = options;

  useEffect(() => {
    if (!enabled) return;
    const listener = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== key.toLowerCase()) return;
      if (meta ? !(event.metaKey || event.ctrlKey) : event.metaKey || event.ctrlKey) return;
      if (!allowInInputs && isTypingTarget(event.target)) return;
      event.preventDefault();
      if (stopPropagation) event.stopPropagation();
      handler(event);
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, [key, meta, allowInInputs, enabled, stopPropagation, handler]);
}
